import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, tap, take } from 'rxjs';

import { ProfileService } from './profile.service';
import { TokensService } from './tokens.service';

import {
  IBillingHistoryData,
  IBillingInfo,
  IUserProfile,
  IUserSubscription
} from 'app/interfaces/profile.interfaces';
import { ITokensPackageResponse } from '../interfaces/token.interfaces';

import { ESubscriptionState } from 'app/enums/purchases.enums';

@Injectable({
  providedIn: 'root'
})
export class ProfileStateService {
  private profileDataSubject = new BehaviorSubject<IUserProfile>(null);
  profileData$ = this.profileDataSubject.asObservable();

  private billingInfoSubject = new BehaviorSubject<IBillingInfo[]>(null);
  billingInfo$ = this.billingInfoSubject.asObservable();

  private userSubscriptionsSubject = new BehaviorSubject<IUserSubscription[]>(null);
  userSubscriptions$ = this.userSubscriptionsSubject.asObservable();

  private transactionsSubject = new BehaviorSubject<IBillingHistoryData>(null);
  transactions$ = this.transactionsSubject.asObservable();

  private tokenPackagesSubject = new BehaviorSubject<ITokensPackageResponse>(null);
  tokenPackages$ = this.tokenPackagesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(true);
  loading$ = this.loadingSubject.asObservable();

  private dataFetched = false;

  constructor(
    private readonly profileService: ProfileService,
    private readonly tokensService: TokensService
  ) {}

  private sortTransactionsByDate(data: IBillingHistoryData): IBillingHistoryData {
    return {
      ...data,
      transactions: data.transactions?.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    };
  }

  /**
   * Prefetches all profile-related data and stores it in the service
   * @returns Observable that emits true if prefetch was successful
   */
  prefetchProfileData(): Observable<boolean> {
    if (this.dataFetched) {
      return of(true);
    }

    this.loadingSubject.next(true);

    return forkJoin({
      profileUserData: this.profileService.getProfileData().pipe(
        catchError(error => {
          console.error('Profile Data Error:', error);
          return of(null);
        })
      ),
      billingInfo: this.profileService.getBillingInfo().pipe(
        catchError(error => {
          console.error('Billing Info Error:', error);
          return of({ data: [] });
        })
      ),
      userSubscriptions: this.profileService.getUserSubscriptions().pipe(
        catchError(error => {
          console.error('User Subscriptions Error:', error);
          return of([]);
        })
      ),
      userTransactions: this.profileService.getAllUserTransactions().pipe(
        map((data: IBillingHistoryData) => this.sortTransactionsByDate(data)),
        catchError(error => {
          console.error('User Transactions Error:', error);
          return of({ transactions: [] });
        })
      ),
      tokenPackages: this.tokensService.getTokensPackage().pipe(
        catchError(error => {
          console.error('Token Packages Error:', error);
          return of({ data: [] });
        })
      ),
      tokensBalance: this.tokensService.tokensBalance$.pipe(
        take(1),
        catchError(error => {
          console.error('Tokens Balance Error:', error);
          return of(null);
        })
      )
    }).pipe(
      tap(
        ({ profileUserData, billingInfo, userSubscriptions, userTransactions, tokenPackages }) => {
          this.profileDataSubject.next(profileUserData);
          this.billingInfoSubject.next(billingInfo.data);

          if (userSubscriptions && userSubscriptions.length) {
            const processedSubscriptions = this.processSubscriptions(userSubscriptions);
            this.userSubscriptionsSubject.next(processedSubscriptions);
          } else {
            this.userSubscriptionsSubject.next([]);
          }

          this.transactionsSubject.next(userTransactions);
          this.tokenPackagesSubject.next(tokenPackages);

          this.dataFetched = true;
          this.loadingSubject.next(false);
        }
      ),
      // Transform the result to a boolean
      map(() => true),
      catchError(error => {
        console.error('Error prefetching profile data:', error);
        this.loadingSubject.next(false);
        return of(false);
      })
    );
  }

  private processSubscriptions(userSubscriptions: IUserSubscription[]): IUserSubscription[] {
    return userSubscriptions.map(subscription => {
      let state: ESubscriptionState;
      const currentTime = new Date();
      const periodEndTime = new Date(subscription.current_period_ends_at);
      const isExpired = periodEndTime <= currentTime;

      if (subscription.auto_renew) {
        state = isExpired ? ESubscriptionState.Expired : ESubscriptionState.Active;
      } else {
        state = isExpired ? ESubscriptionState.Expired : ESubscriptionState.Expiring;
      }

      return { ...subscription, state };
    });
  }

  /**
   * Update profile data with new values
   * @param profileData The updated profile data
   */
  updateProfileSection<T>(profileData: T): Observable<T> {
    return this.profileService.updateProfileData<T>(profileData).pipe(
      tap(() => {
        this.refreshProfileData();
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  refreshProfileData(): void {
    this.profileService
      .getProfileData()
      .pipe(
        catchError(error => {
          console.error('Profile Data Error:', error);
          return of(null);
        })
      )
      .subscribe(data => {
        if (data) {
          this.profileDataSubject.next(data);
        }
      });
  }

  refreshBillingInfo(): void {
    this.profileService
      .getBillingInfo()
      .pipe(
        catchError(error => {
          console.error('Billing Info Error:', error);
          return of({ data: [] });
        })
      )
      .subscribe(data => {
        this.billingInfoSubject.next(data.data);
      });
  }

  refreshUserSubscriptions(): void {
    this.profileService
      .getUserSubscriptions()
      .pipe(
        catchError(error => {
          console.error('User Subscriptions Error:', error);
          return of([]);
        })
      )
      .subscribe(subscriptions => {
        if (subscriptions && subscriptions.length) {
          const processedSubscriptions = this.processSubscriptions(subscriptions);
          this.userSubscriptionsSubject.next(processedSubscriptions);
        } else {
          this.userSubscriptionsSubject.next([]);
        }
      });
  }

  refreshTransactions(): void {
    this.profileService
      .getAllUserTransactions()
      .pipe(
        map((data: IBillingHistoryData) => this.sortTransactionsByDate(data)),
        catchError(error => {
          console.error('User Transactions Error:', error);
          return of({ transactions: [] });
        })
      )
      .subscribe(transactions => {
        this.transactionsSubject.next(transactions);
      });
  }

  clearProfileData(): void {
    this.profileDataSubject.next(null);
    this.billingInfoSubject.next(null);
    this.userSubscriptionsSubject.next(null);
    this.transactionsSubject.next(null);
    this.tokenPackagesSubject.next(null);
    this.loadingSubject.next(true);
    this.dataFetched = false;
  }
}
